import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

function formatDate(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ManageUsers({ currentUser }) {
  const [users, setUsers] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/api/users`)
      .then(res => setUsers(res.data.users))
      .catch(err => setError(err.response?.data?.error || err.message));
  };

  useEffect(load, []);

  const addUser = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/users`, { email: newEmail.trim() });
      setUsers(res.data.users);
      setNewEmail('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (id) => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.delete(`${API}/api/users/${id}`);
      setUsers(res.data.users);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap>
      <Header>
        <Eyebrow>Access control</Eyebrow>
        <Title>Manage users</Title>
        <Subtitle>Only these Google accounts can sign in to Build Tool</Subtitle>
      </Header>

      <AddForm onSubmit={addUser}>
        <AddInput
          type="email"
          placeholder="name@example.com"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          disabled={busy}
        />
        <AddBtn type="submit" disabled={busy || !newEmail.trim()}>Add</AddBtn>
      </AddForm>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Added</th>
            <th>Last login</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(users || []).map(u => (
            <tr key={u.id}>
              <td>
                <Email>{u.email}</Email>
                {currentUser?.email?.toLowerCase() === u.email.toLowerCase() && <YouTag>you</YouTag>}
              </td>
              <td><Muted mono>{formatDate(u.created_at)}</Muted></td>
              <td><Muted mono>{formatDate(u.last_login_at)}</Muted></td>
              <td style={{ textAlign: 'right' }}>
                <RemoveBtn
                  disabled={busy || currentUser?.email?.toLowerCase() === u.email.toLowerCase()}
                  onClick={() => removeUser(u.id)}
                  title={currentUser?.email?.toLowerCase() === u.email.toLowerCase() ? "You can't remove your own access" : 'Remove access'}
                >
                  Remove
                </RemoveBtn>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {users && users.length === 0 && <EmptyState>No users yet.</EmptyState>}
    </Wrap>
  );
}

const Wrap = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 28px 32px 60px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Eyebrow = styled.p`
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--accent-text);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.3px;
  color: var(--text);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-top: 4px;
`;

const AddForm = styled.form`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const AddInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--bg-2);
  font-size: 13px;
  color: var(--text);
  font-family: var(--font-mono);
  transition: border-color 0.15s;
  &:focus { border-color: var(--accent); }
  &::placeholder { color: var(--text-3); }
`;

const AddBtn = styled.button`
  padding: 10px 20px;
  background: var(--accent);
  color: #ffffff;
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 13px;
  border-radius: var(--radius);
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;

  thead th {
    text-align: left;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-3);
    font-weight: 500;
    padding: 9px 14px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-3);
  }

  tbody td {
    padding: 10px 14px;
    font-size: 13px;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: none; }
`;

const Email = styled.span`
  color: var(--text);
  font-weight: 500;
`;

const YouTag = styled.span`
  margin-left: 8px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--accent-text);
  background: var(--accent-dim);
  padding: 2px 7px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Muted = styled.span`
  color: var(--text-3);
  font-size: 12px;
  font-family: ${p => p.mono ? 'var(--font-mono)' : 'inherit'};
`;

const RemoveBtn = styled.button`
  background: none;
  border: 1px solid var(--border);
  color: var(--text-2);
  font-size: 11px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: var(--radius);
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: var(--red); color: var(--red); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-3);
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 10px 14px;
  border-radius: var(--radius);
  margin-bottom: 16px;
  font-size: 13px;
`;

export default ManageUsers;
